import React, { useState, useEffect, useRef } from 'react';
import { getPendingEvents, undoEventAction } from '../api';
import ItemEventoPendiente from './ItemEventoPendiente';
import ToastDeshacer from './ToastDeshacer';

function mensajeSerie(seriesResult) {
  if (!seriesResult) return null;
  const conflictos = seriesResult.conflicts || [];
  const partes = [];
  if (seriesResult.resolved > 1) {
    partes.push(`Se aplicó a ${seriesResult.resolved} eventos de la serie.`);
  }
  if (conflictos.length > 0) {
    // Genérico a propósito — no vale la pena distinguir singular/plural acá,
    // lo importante es que la resolución parcial no quede invisible.
    partes.push(`${conflictos.length} evento(s) de la serie sin resolver por choque de horario.`);
  }
  return partes.length ? { texto: partes.join(' '), tieneConflictos: conflictos.length > 0 } : null;
}

// Fecha de orden de un ítem de la bandeja ya agrupada — next_start para una
// serie, start para uno suelto (ver linking.group_pending_events, backend).
function fechaOrden(item) {
  return new Date(item.is_series ? item.next_start : item.start).getTime();
}

function porFecha(a, b) {
  return fechaOrden(a) - fechaOrden(b);
}

// Bandeja de eventos de Google Calendar pendientes de revisar
// (needs_review=True). Abierta desde la campanita de Navbar. La lista de
// pacientes se recibe de App.js (ya cargada por el sidebar) en vez de
// pedirla de nuevo acá.
//
// pending/ (etapa 3c) ya viene agrupado por serie recurrente — cada ítem de
// `eventos` es o bien un resumen de serie (is_series:true) o un evento suelto
// (is_series:false), nunca una lista plana de filas individuales como en la
// 3b. Resolver cualquiera de los dos saca exactamente UN ítem del array, así
// que ya no hace falta buscar "hermanos" a mano — eso ya lo hizo el backend.
function ModalBandejaGoogle({ pacientes, onCerrar, onResueltos, onPacientesActualizados, onTurnosActualizados }) {
  const [eventos, setEventos] = useState(null); // null = todavía no cargó
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [mensaje, setMensaje] = useState(null);
  // Cartel de "deshacer": { key, mensaje, item } — `item` es la copia exacta
  // del ítem tal como estaba en la lista antes de sacarlo, necesaria para
  // reinsertarlo si el usuario deshace (undo/ no la devuelve reconstruida).
  // Se descarta al cerrarse el cartel (timeout, deshacer, o cerrar el modal)
  // — no hace falta que sobreviva más allá de esa ventana.
  const [toast, setToast] = useState(null);
  const toastKeyRef = useRef(0);

  useEffect(() => {
    let cancelado = false;
    getPendingEvents()
      .then((data) => { if (!cancelado) setEventos(data); })
      .catch(() => { if (!cancelado) setErrorCarga('No se pudo cargar la bandeja de eventos pendientes.'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, []);

  const handleResuelto = (item, mensajeToast, data) => {
    const seriesResult = data?.series_result;

    setEventos(prev => {
      if (!prev) return prev;
      let siguiente = prev.filter(e => e.id !== item.id);

      // Si quedaron instancias de la serie sin resolver (choque de
      // solapamiento — nunca pasa con ignore/reject, que no crean turnos),
      // esas siguen pendientes de verdad en el backend: hay que reinsertar
      // un resumen de serie para ellas en vez de dejarlas invisibles hasta
      // el próximo reload completo.
      const conflictos = seriesResult?.conflicts || [];
      if (conflictos.length > 0) {
        const proximoConflicto = conflictos.reduce(
          (min, c) => (!min || c.start < min.start ? c : min), null,
        );
        siguiente = [
          ...siguiente,
          {
            is_series: true,
            id: proximoConflicto.id,
            recurring_event_id: item.recurring_event_id,
            title: item.title,
            next_start: proximoConflicto.start,
            is_all_day: item.is_all_day,
            count: conflictos.length,
          },
        ];
      }

      siguiente.sort(porFecha);
      return siguiente;
    });

    if (onResueltos) onResueltos(1);
    // link/link-new-patient/mark-as-event pueden haber creado un Appointment
    // visible en el calendario de la app -- Calendario.jsx/PendientesDelDia.jsx
    // no lo saben solos (no hay refetch automático), así que hay que avisarles
    // explícitamente. reject nunca crea uno, pero el chequeo de `data.appointment`
    // ya lo excluye sin necesitar una rama aparte por acción.
    if (onTurnosActualizados && data?.appointment) onTurnosActualizados();

    const msg = mensajeSerie(seriesResult);
    if (msg) {
      setMensaje(msg);
      if (!msg.tieneConflictos) {
        setTimeout(() => setMensaje(null), 4000);
      }
    }

    toastKeyRef.current += 1;
    setToast({ key: toastKeyRef.current, mensaje: mensajeToast, item });
  };

  const cerrarToast = () => setToast(null);

  const handleDeshacer = async () => {
    if (!toast) return;
    await undoEventAction(toast.item.id);
    setEventos(prev => {
      if (!prev) return prev;
      if (prev.some(e => e.id === toast.item.id)) return prev; // ya está, no duplicar
      const siguiente = [...prev, toast.item];
      siguiente.sort(porFecha);
      return siguiente;
    });
    if (onResueltos) onResueltos(-1);
    // Deshacer un link/link-new-patient/mark-as-event cancela (soft-delete)
    // el Appointment creado -- mismo aviso que handleResuelto, sin filtrar
    // por acción para no duplicar esa lógica acá (un refetch de más para un
    // reject deshecho es inofensivo).
    if (onTurnosActualizados) onTurnosActualizados();
  };

  const cerrarModal = () => {
    setToast(null);
    onCerrar();
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && cerrarModal()}>
      <div className="modal-card modal-card-wide">
        <div className="modal-header">
          <h3>Eventos de Google Calendar</h3>
          <button className="modal-btn-cerrar" onClick={cerrarModal}>✕</button>
        </div>
        <div className="modal-body">
          {mensaje && (
            <div className={mensaje.tieneConflictos ? 'modal-mensaje-error' : 'modal-mensaje-ok'}>
              {mensaje.texto}
            </div>
          )}

          {cargando && <p className="gcal-info-texto">Cargando eventos pendientes...</p>}
          {errorCarga && <p className="gcal-info-texto">{errorCarga}</p>}

          {!cargando && !errorCarga && eventos && eventos.length === 0 && (
            <p className="sesiones-vacio">No tenés eventos pendientes de revisar.</p>
          )}

          {!cargando && eventos && eventos.length > 0 && (
            <div className="bandeja-lista">
              {eventos.map(evento => (
                <ItemEventoPendiente
                  key={evento.id}
                  evento={evento}
                  pacientes={pacientes}
                  onResuelto={handleResuelto}
                  onPacientesActualizados={onPacientesActualizados}
                />
              ))}
            </div>
          )}
        </div>
        {toast && (
          <ToastDeshacer
            key={toast.key}
            mensaje={toast.mensaje}
            onDeshacer={handleDeshacer}
            onCerrar={cerrarToast}
          />
        )}
      </div>
    </div>
  );
}

export default ModalBandejaGoogle;
